import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from '../cn'
import { SearchInput } from '../SearchInput'
import { useDebouncedValue } from '../useDebouncedValue'
import { FilterChips } from './FilterChips'
import { FilterEditorDialog } from './FilterEditors'
import type { CollectionFilterDef, UseCollectionResult } from './useCollection'

export interface CollectionToolbarProps<RowT> {
  collection: UseCollectionResult<RowT>
  /** When false, omits the search field (status filters and result count remain). */
  searchable?: boolean
  searchPlaceholder?: string
  searchAriaLabel?: string
  searchDebounceMs?: number
  searchShortcut?: string | false
  showPresets?: boolean
  primaryAction?: ReactNode | ((helpers: { flushPendingSearch: () => void }) => ReactNode)
  stableChipNames?: boolean
  className?: string
}

interface SearchShortcutEntry {
  token: symbol
  shortcut: string
  focus: () => void
}

const searchShortcutRegistry: SearchShortcutEntry[] = []

export function CollectionToolbar<RowT>({
  collection,
  searchable = true,
  searchPlaceholder = 'Search rows',
  searchAriaLabel = 'Search collection',
  searchDebounceMs = 250,
  searchShortcut = '/',
  showPresets = true,
  primaryAction,
  stableChipNames = false,
  className = '',
}: CollectionToolbarProps<RowT>) {
  const [query, setQuery] = useState(collection.state.q)
  const debouncedQuery = useDebouncedValue(query, searchDebounceMs)
  const lastAppliedQuery = useRef(collection.state.q)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [openFilter, setOpenFilter] = useState<string | null>(null)

  const flushPendingSearch = useCallback(() => {
    collection.setQuery(query)
    lastAppliedQuery.current = query
  }, [collection.setQuery, query])

  useEffect(() => {
    setQuery(collection.state.q)
    lastAppliedQuery.current = collection.state.q
  }, [collection.state.q])

  useEffect(() => {
    if (debouncedQuery === lastAppliedQuery.current) return
    collection.setQuery(debouncedQuery)
    lastAppliedQuery.current = debouncedQuery
  }, [collection.setQuery, debouncedQuery])

  useEffect(() => {
    if (!searchable || searchShortcut === false || !searchShortcut) return undefined
    const entry: SearchShortcutEntry = {
      token: Symbol('collection-search-shortcut'),
      shortcut: searchShortcut,
      focus: () => searchInputRef.current?.focus(),
    }
    searchShortcutRegistry.push(entry)
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (searchShortcutRegistry[0] !== entry) return
      if (event.key !== entry.shortcut || event.isComposing || isEditableTarget(event.target)) return
      event.preventDefault()
      entry.focus()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      const index = searchShortcutRegistry.indexOf(entry)
      if (index >= 0) searchShortcutRegistry.splice(index, 1)
    }
  }, [searchShortcut, searchable])

  const primaryActionNode =
    typeof primaryAction === 'function' ? primaryAction({ flushPendingSearch }) : primaryAction
  const renderPresets = showPresets && collection.presets.list.length > 0

  return (
    <div className={cn('space-y-3 border-b border-line-strong bg-white px-4 py-3', className)}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        {renderPresets && <PresetChips collection={collection} />}
        {searchable ? (
          <div role="search" aria-label="Collection search" className="w-full max-w-sm">
            <SearchInput
              ref={searchInputRef}
              shape="pill"
              size="sm"
              debounceMs={0}
              ariaLabel={searchAriaLabel}
              placeholder={searchPlaceholder}
              value={query}
              onChange={setQuery}
              className="w-full max-w-sm"
            />
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          {collection.filterDefs.map((filter) => (
            <FilterButton key={filter.id} collection={collection} filter={filter} open={openFilter === filter.id} onOpen={() => setOpenFilter(filter.id)} onClose={() => setOpenFilter(null)} />
          ))}
          {collection.views.enabled && (
            <SavedViews collection={collection} />
          )}
        </div>
        <div className="flex items-center gap-3 md:ml-auto">
          <span className="text-xs font-medium text-ink-muted" aria-live="polite">
            {collection.uiState === 'loading' ? 'Loading...' : resultCountLabel(collection.total)}
          </span>
          {primaryActionNode}
        </div>
      </div>
      <FilterChips collection={collection} stableChipNames={stableChipNames} />
    </div>
  )
}

function PresetChips<RowT>({ collection }: { collection: UseCollectionResult<RowT> }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {collection.presets.list.map((preset) => {
        const active = collection.presets.activeId === preset.id
        return (
          <button
            key={preset.id}
            type="button"
            aria-pressed={active}
            onClick={() => active ? collection.presets.clear() : collection.presets.apply(preset.id)}
            className={cn(
              'min-h-tap rounded-control border px-3 text-xs font-semibold transition-colors duration-fast ease-out-soft focus-visible:outline-none focus-visible:shadow-focus',
              active ? 'border-link bg-link text-white' : 'border-line bg-white text-ink-secondary hover:bg-whisper',
            )}
          >
            {preset.label}
          </button>
        )
      })}
    </div>
  )
}

function FilterButton<RowT>({ collection, filter, open, onOpen, onClose }: { collection: UseCollectionResult<RowT>; filter: CollectionFilterDef<RowT>; open: boolean; onOpen: () => void; onClose: () => void }) {
  const active = collection.activeFilters.some((item) => item.id === filter.id)
  const buttonRef = useRef<HTMLButtonElement>(null)
  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={open ? onClose : onOpen}
        className={cn(
          'min-h-tap rounded-control border px-3 text-xs font-semibold transition-colors duration-fast ease-out-soft focus-visible:outline-none focus-visible:shadow-focus',
          active ? 'border-mustard bg-mustard-tint text-strong' : 'border-line bg-white text-ink-secondary hover:bg-whisper',
        )}
      >
        {filter.label}
      </button>
      <FilterEditorDialog collection={collection} filter={filter} open={open} onClose={onClose} anchorRef={buttonRef} />
    </div>
  )
}

function SavedViews<RowT>({ collection }: { collection: UseCollectionResult<RowT> }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [renameId, setRenameId] = useState<string | null>(null)
  const [renameName, setRenameName] = useState('')

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="min-h-tap rounded-control border border-line px-3 text-xs font-semibold text-ink-secondary hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus"
      >
        Saved views
      </button>
      {open && (
        <div role="dialog" aria-label="Saved views" className="absolute right-0 z-dropdown mt-2 w-80 rounded-control border border-line bg-white p-3 text-sm shadow-pop">
          <label className="block text-xs font-medium text-ink-muted">
            View name
            <input value={name} onChange={(event) => setName(event.target.value)} className="mt-1 min-h-tap w-full rounded-control border border-line px-3 text-sm text-ink-body focus-visible:outline-none focus-visible:shadow-focus" />
          </label>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => {
                if (!name.trim()) return
                collection.views.save(name.trim())
                setName('')
              }}
              className="min-h-tap rounded-control bg-mustard px-3 text-xs font-semibold text-ink hover:bg-mustard-600 focus-visible:outline-none focus-visible:shadow-focus"
            >
              Save view
            </button>
          </div>
          <div className="mt-3 max-h-64 space-y-2 overflow-auto border-t border-line pt-3">
            {collection.views.saved.length === 0 ? <p className="m-0 text-xs text-ink-muted">No saved views</p> : null}
            {collection.views.saved.map((view) => {
              const renaming = renameId === view.id
              return (
                <div key={view.id} className="rounded-control border border-line p-2">
                  {renaming ? (
                    <label className="block text-xs font-medium text-ink-muted">
                      Rename view
                      <input value={renameName} onChange={(event) => setRenameName(event.target.value)} className="mt-1 min-h-tap w-full rounded-control border border-line px-2 text-sm text-ink-body focus-visible:outline-none focus-visible:shadow-focus" />
                    </label>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <button type="button" onClick={() => collection.views.apply(view.id)} className="min-h-8 rounded px-2 text-left text-xs font-semibold text-ink-body hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus">
                        {view.name}
                      </button>
                      {collection.views.defaultId === view.id && <span className="text-micro font-semibold uppercase text-ink-muted">Default</span>}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    {renaming ? (
                      <button
                        type="button"
                        onClick={() => {
                          collection.views.rename(view.id, renameName)
                          setRenameId(null)
                          setRenameName('')
                        }}
                        className="min-h-8 rounded-control border border-line px-2 text-xs text-ink-body hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus"
                      >
                        Save
                      </button>
                    ) : (
                      <button type="button" onClick={() => { setRenameId(view.id); setRenameName(view.name) }} className="min-h-8 rounded-control border border-line px-2 text-xs text-ink-body hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus">
                        Rename
                      </button>
                    )}
                    <button type="button" onClick={() => collection.views.setDefault(collection.views.defaultId === view.id ? null : view.id)} className="min-h-8 rounded-control border border-line px-2 text-xs text-ink-body hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus">
                      {collection.views.defaultId === view.id ? 'Clear default' : 'Set default'}
                    </button>
                    <button type="button" onClick={() => collection.views.remove(view.id)} className="min-h-8 rounded-control border border-line px-2 text-xs text-ink-body hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus">
                      Delete
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName.toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable
}

function resultCountLabel(total: number): string {
  return total === 1 ? '1 result' : `${total} results`
}
